import React, { useEffect, useState } from 'react'
import clsx from 'clsx'
import { useAtom, useSetAtom } from 'jotai'
import isEmpty from 'lodash-es/isEmpty'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Box,
  Button,
  Box as ContactInfoContainer,
  Box as Container,
  EmptyState,
  Icon,
  RowsSkeletonLoader,
} from '@node-space/ui-components'
import { Lottie } from 'components/Animations/SimpleLottie'
import { PageHeader } from 'components/header/PageHeader'
import { MIN_PAGINATION_SIZE } from 'constants/queries'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useContactInstrumentQuery } from 'hooks/queries/useContactInstrumentsQuery'
import { useContactQuery } from 'hooks/queries/useContactQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ContactInstrumentActionId, ContactInstrumentActionIds } from 'types/atoms/atom.contacts'
import { contactAtom, contactInstrumentActionAtom } from './atoms/contactAtoms'
import { ContactInfo } from './components/ContactInfo'
import { ContactInstrumentsSheet } from './ContactInstruments'
import { ContactInstrumentsList } from './ContactInstrumentsList'

export const ContractInstrument = () => {
  const { t } = useTranslation()
  const { enableContactBankAccounts } = useFeatureFlags()
  const { viewOnly } = useProfileContext() || {}
  const permissions = useMappedRoles()?.contacts
  const canEdit = permissions?.canEdit
  const { id }: { id?: number } = useParams()

  const { data: contact, isError: isContactError } = useContactQuery(id?.toString())
  const {
    data: { content: instrumentsList } = {},
    isFetching,
    isError,
  } = useContactInstrumentQuery({
    contactId: id?.toString(),
  })
  const [isContactInstrumentSheetOpen, setIsContactInstrumentSheetOpen] = useState(false)

  const [contactInstrumentAction, setContactInstrumentAction] = useAtom(contactInstrumentActionAtom)
  const setContact = useSetAtom(contactAtom)

  useEffect(() => {
    if (!isEmpty(contact)) {
      setContact(contact)
    }
  }, [contact])

  const triggerOpenSheet = ({
    actionId,
    header,
  }: {
    actionId: ContactInstrumentActionId
    header: string
  }) => {
    setContact(contact)
    setContactInstrumentAction({
      ...contactInstrumentAction,
      actionId: actionId,
      header: header,
      onClose: () => {
        setIsContactInstrumentSheetOpen(false)
        setContactInstrumentAction(null)
      },
    })
    setIsContactInstrumentSheetOpen(true)
  }

  const openCreateCryptoAccountSheet = () => {
    triggerOpenSheet({
      actionId: ContactInstrumentActionIds?.INSTRUMENT,
      header: t('contacts.addCryptoAccount'),
    })
  }

  const openCreateBankAccountSheet = () => {
    triggerOpenSheet({
      actionId: ContactInstrumentActionIds?.FIAT_INSTRUMENT,
      header: t('contacts.addBankAccount'),
    })
  }

  return (
    <>
      <PageHeader
        description={t('contacts.contactDetailsDescription', { contactName: contact?.name })}
        renderRightSection={
          canEdit &&
          !isFetching &&
          !!instrumentsList?.length && (
            <Box flex gapX={16}>
              <Button
                testId="add-contact-instrument"
                variant="secondary"
                disabled={viewOnly}
                onClick={openCreateCryptoAccountSheet}
                iconElement={<Icon name="Plus" />}
              >
                {t('contacts.cryptoAccount')}
              </Button>
              {enableContactBankAccounts && (
                <Button
                  testId="add-contact-bank-account-instrument"
                  variant="secondary"
                  disabled={viewOnly}
                  onClick={openCreateBankAccountSheet}
                  iconElement={<Icon name="Plus" />}
                >
                  {t('contacts.bankAccount')}
                </Button>
              )}
            </Box>
          )
        }
      />

      <Container flexCol className="md:flex-row" gapX={16} gapY={16} width="full">
        <Box
          flexCol
          width="full"
          className={clsx({
            'break-words order-2 md:order-none': !isError && !isFetching,
          })}
        >
          {/* Show error state */}
          {isError && !isFetching && (
            <EmptyState
              testId="error-state"
              LottiePlayer={<Lottie src="connection-error" />}
              title={t('defaultError')}
              description={
                <>
                  {`${t('requestErrors.comeBackLater')} `}
                  <Button variant="ghost" noPadding onClick={() => window.location.reload()}>
                    {t('requestErrors.refreshPage')}
                  </Button>
                </>
              }
            />
          )}

          {/* Show content state */}
          {!isError && (
            <>
              {isFetching && <RowsSkeletonLoader rows={MIN_PAGINATION_SIZE} />}

              {!!instrumentsList?.length && !isFetching && (
                <Box className="w-full">
                  <ContactInstrumentsList
                    contactInstruments={instrumentsList}
                    setIsContactInstrumentSheetOpen={setIsContactInstrumentSheetOpen}
                  />
                </Box>
              )}
            </>
          )}

          {/* Show empty state */}
          {!isFetching && !isError && !instrumentsList?.length && (
            <EmptyState
              testId="empty-state"
              LottiePlayer={<Lottie src="no-data" />}
              title={t(
                enableContactBankAccounts
                  ? 'contacts.noContactInstrumentHeadlineV2'
                  : 'contacts.noContactInstrumentHeadline',
                {
                  contactName: contact?.name,
                }
              )}
              description={t(
                enableContactBankAccounts
                  ? 'contacts.addContactInstrumentDescriptionV2'
                  : 'contacts.addContactInstrumentDescription'
              )}
              PrimaryButton={
                <Button
                  iconElement={<Icon name="Plus" />}
                  data-testId="add-contact-instrument"
                  disabled={viewOnly || !canEdit}
                  onClick={openCreateCryptoAccountSheet}
                >
                  {t('contacts.cryptoAccount')}
                </Button>
              }
              SecondaryButton={
                enableContactBankAccounts && (
                  <Button
                    testId="add-contact-bank-account-instrument"
                    disabled={viewOnly || !canEdit}
                    onClick={openCreateBankAccountSheet}
                    iconElement={<Icon name="Plus" />}
                  >
                    {t('contacts.bankAccount')}
                  </Button>
                )
              }
            />
          )}
        </Box>

        {!isEmpty(contact) && !isContactError && (
          <ContactInfoContainer
            flexCol
            className="max-w-[320px] min-w-[260px] w-full order-1 md:order-none"
          >
            <ContactInfo />
          </ContactInfoContainer>
        )}
      </Container>
      <ContactInstrumentsSheet isSheetOpen={isContactInstrumentSheetOpen} />
    </>
  )
}
